import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Avatar,
  Chip,
  InputAdornment,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import UserService, { UserSearchResult } from '../services/userService';

// Create our own debounce function to avoid lodash dependency issues
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T & { cancel: () => void } => {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
};

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<UserSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Debounced search function for suggestions
  const debouncedGetSuggestions = useMemo(
    () => debounce(async (searchQuery: string) => {
      if (searchQuery.trim().length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const suggestionsData = await UserService.getUserSuggestions(searchQuery, 5);
        setSuggestions(suggestionsData);
      } catch (error) {
        console.error('Failed to get suggestions:', error);
        setSuggestions([]);
      }
    }, 300),
    []
  );

  // Debounced search function for main results
  const debouncedSearch = useMemo(
    () => debounce(async (searchQuery: string) => {
      if (searchQuery.trim().length < 2) {
        setResults([]);
        setSearchPerformed(false);
        return;
      }

      setLoading(true);
      setSearchPerformed(true);

      try {
        const searchData = await UserService.searchUsers(searchQuery, 1, 20);
        setResults(searchData.users);
      } catch (error) {
        console.error('Failed to search users:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  // Handle input change
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = event.target.value;
    setQuery(newQuery);

    // Update URL
    if (newQuery.trim()) {
      setSearchParams({ q: newQuery });
    } else {
      setSearchParams({});
    }

    // Get suggestions and perform search
    debouncedGetSuggestions(newQuery);
    debouncedSearch(newQuery);
  }, [setSearchParams, debouncedGetSuggestions, debouncedSearch]);

  // Handle user profile navigation
  const handleUserClick = useCallback((userId: string) => {
    navigate(`/users/${userId}`);
  }, [navigate]);

  // Clear search
  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setSuggestions([]);
    setSearchPerformed(false);
    setSearchParams({});
  }, [setSearchParams]);

  // Initialize search from URL params
  useEffect(() => {
    const initialQuery = searchParams.get('q');
    if (initialQuery) {
      setQuery(initialQuery);
      debouncedSearch(initialQuery);
    }
  }, [searchParams, debouncedSearch]);

  // Cleanup debounced functions
  useEffect(() => {
    return () => {
      debouncedGetSuggestions.cancel();
      debouncedSearch.cancel();
    };
  }, [debouncedGetSuggestions, debouncedSearch]);

  const renderUserCard = (user: UserSearchResult) => (
    <Card
      key={user.id}
      sx={{
        mb: 1,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-1px)'
        },
        transition: 'all 0.2s ease'
      }}
      onClick={() => handleUserClick(user.id)}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            width: 48,
            height: 48,
            bgcolor: '#1877f2',
            fontSize: '1.2rem',
            fontWeight: 600
          }}>
            {user.profilePicture || user.name.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              fontSize: '1rem',
              color: '#1877f2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user.name}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.5
            }}>
              {user.email}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={user.role === 'Faculty' ? <SchoolIcon /> : <PersonIcon />}
                label={user.role}
                size="small"
                color={user.role === 'Faculty' ? 'secondary' : 'primary'}
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: 24 }}
              />

              {user.branch && (
                <Chip
                  label={user.branch}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              )}

              {user.clubCount !== undefined && user.clubCount > 0 && (
                <Chip
                  label={`${user.clubCount} clubs`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderSuggestionItem = (user: UserSearchResult) => (
    <ListItem key={user.id} disablePadding>
      <ListItemButton onClick={() => handleUserClick(user.id)}>
        <ListItemAvatar>
          <Avatar sx={{
            width: 36,
            height: 36,
            bgcolor: '#1877f2',
            fontSize: '0.9rem',
            fontWeight: 600
          }}>
            {user.profilePicture || user.name.charAt(0).toUpperCase()}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user.name}
            </Typography>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
              <Chip
                label={user.role}
                size="small"
                color={user.role === 'Faculty' ? 'secondary' : 'primary'}
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 }
      }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h5" sx={{
            fontWeight: 700,
            color: '#1877f2',
            mb: 2,
            fontSize: { xs: '1.3rem', sm: '1.5rem' }
          }}>
            Search People
          </Typography>

          {/* Search Input */}
          <TextField
            fullWidth
            value={query}
            onChange={handleInputChange}
            placeholder="Search for people by name or email..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loading && <CircularProgress size={20} />}
                    <ClearIcon
                      sx={{ cursor: 'pointer', color: 'action.active' }}
                      onClick={handleClear}
                    />
                  </Box>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#f8f9fa',
                '& fieldset': {
                  borderColor: '#e4e6ea'
                },
                '&:hover fieldset': {
                  borderColor: '#1877f2'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1877f2'
                }
              }
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        {/* Live Suggestions */}
        {query.length >= 2 && suggestions.length > 0 && !searchPerformed && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'text.secondary' }}>
                  Suggestions
                </Typography>
              </Box>
              <List sx={{ pt: 0 }}>
                {suggestions.map(renderSuggestionItem)}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Search Results */}
        {searchPerformed && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : results.length > 0 ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Search Results ({results.length})
                  </Typography>
                  <Divider />
                </Box>

                <Box>
                  {results.map(renderUserCard)}
                </Box>
              </>
            ) : (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
                    🔍
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    No results found
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Try searching with different keywords or check for typos
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Welcome State */}
        {!searchPerformed && query.length < 2 && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
                👋
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Search for People
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Find students and faculty members by name or email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start typing to see suggestions and search results
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default SearchPage;